/* eslint-disable no-unused-vars */
import { Box, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseRegistration } from '../../../core/baseRegistration';
import { useState } from 'react';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { Logout, loginUsingToken, getUser, getUserFromToken } from '../../../../../services/authService';
import { Toaster } from '../../../../../shared/components/common/toaster';
import { UserService } from '../../../../../services/userService';
import { useEffect } from 'react';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { EligibilityChecker } from '../eligibilityChecker';
import { LocalStorageKey } from '../../../../../constants/localStorageKeys';
import { IS_SA } from '../../../../../config/appConfig';
import { Platform } from '../../../components/platform';
import { LanguageService } from '../../../../../services/languageService';
import { SignupPageWrapper } from '../../../core/signUpPageWrapper';
import { identifyOpenPanel, trackOpenPanel, trackOpenPanelView } from '../../../../../helpers/openPanelHelper';
import { OpenPanelCategories, OpenPanelEvents } from '../../../../../constants/openPanelEvents';

export const BorrowerBaseRegistration = ({
	regCountryList = [],
	isSA = IS_SA,
}) => {
	const userService = new UserService();
	const languageService = new LanguageService();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [showOTPAuth, setShowOTPAuth] = useState(false);

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [email, setEmail] = useState(null);
	const [showEligibility, setShowEligibility] = useState(
		// Skip eligibility checker for KSA, only show for Global
		!isSA && sessionStorage.getItem(LocalStorageKey.isEligibilityCompleted) !== '1'
	);

	// Only once the signup form is actually on screen (docx §5: "user begins the
	// sign-up form"). On Global this component first renders the EligibilityChecker,
	// whose pass branch does a window.location.reload() — that reload wipes
	// trackOpenPanelView's in-memory dedupe, so firing on bare mount would send
	// signup_started twice for every Global signup and double the funnel entry count.
	useEffect(() => {
		if (showEligibility) {
			return;
		}
		trackOpenPanelView(OpenPanelEvents.signupStarted, {
			page_name: 'Sign up',
			category: OpenPanelCategories.onboarding,
		});
	}, [showEligibility]);

	useEffect(() => {
		setEmail(searchParams.get('email'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams.get('email')]);

	const onNavigateToNextScreen = () => {
		let route = IS_SA
			? AppRoutes.IRegNafathVerification
			: AppRoutes.BRegPersonalInfo;
		navigate(buildLocalizeRoute(`${AppRoutes.BorrowerRegistartion}/${route}`));
	};

	const isAr = () => languageService.isArabic();

	const onSubmit = (values) => {
		if (!values.termsConditionsForInvestmentsAccepted || !values?.shariahTandCAccepted) {
			Toaster({ msg: t('termsAndConditionsRequired') });
			return;
		}

		setLoading(true);
		userService
			.registerBorrower(values)
			.then((resp) => {
				sessionStorage.setItem(LocalStorageKey.isEligibilityCompleted, '0');
				let tokenModel = resp.data;
				if (tokenModel) {
					setLoading(false);
					trackOpenPanel(OpenPanelEvents.borrowerSignupCompleted, {
						category: OpenPanelCategories.onboarding,
					});
					// These describe the account that was just created, so they belong on
					// both branches — the event above fires either way, and nothing
					// downstream backfills them. Only the identity lookup differs, because
					// the unconfirmed-email path deliberately does not log the user in.
					const signupTraits = {
						account_status: 'pending',
						...(values?.contactNumber?.code && values?.contactNumber?.number
							? { mobile_number: `+${values.contactNumber.code}${values.contactNumber.number}` }
							: {}),
					};
					if (!resp.data?.emailConfirmed) {
						// getUser() reads the saved token and nothing has been saved yet
						// here, so decode the one the API just returned.
						identifyOpenPanel(getUserFromToken(tokenModel.token), signupTraits);
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(values.email)}&type=2`));
					} else {
						loginUsingToken(tokenModel.token);
						// After loginUsingToken, which issues its own trait-less identify.
						identifyOpenPanel(getUser(), signupTraits);
						if (tokenModel.otpRequested) {
							setShowOTPAuth(true);
						} else {
							onNavigateToNextScreen();
						}
					}

				}
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	return (
		<Box className="justify-center items-center flex">
			{!showEligibility ? (
				<SignupPageWrapper user='borrower'>
					<BaseRegistration
						defaultEmail={email}
						loading={loading}
						clientType={'borrower'}
						onSubmitForm={(values) => {
							onSubmit(values);
						}}></BaseRegistration>
				</SignupPageWrapper>

			) : (
				<EligibilityChecker
					isSA={isSA}
					regCountryList={regCountryList}
					onSubmit={() => {
						window.location.reload();
					}}
				/>
			)}

			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Authentication}
					onClose={(isVerified) => {
						setShowOTPAuth(false);
						if (isVerified) {
							onNavigateToNextScreen();
						} else {
							Logout();
							navigate(buildLocalizeRoute(AppRoutes.Login));
						}
					}}
					state={showOTPAuth}></OTPAuthenticationDialog>
			) : null}
		</Box>
	);
};
